import type { FormEvent, ReactNode } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlatformLockWall } from "./PlatformLockWall";

interface PlatformLockStatus {
  closed: boolean;
  unlocked: boolean;
}

export function shouldRenderPlatformLockWall(args: {
  isLoading: boolean;
  isError: boolean;
  status: PlatformLockStatus | undefined;
}): boolean {
  if (args.isLoading || args.isError) return false;
  return args.status?.closed === true && args.status.unlocked === false;
}

async function getPlatformLockStatus(): Promise<PlatformLockStatus> {
  const res = await fetch("/api/platform-lock/status", {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Greška pri provjeri statusa platforme");
  return res.json() as Promise<PlatformLockStatus>;
}

async function unlockPlatform(password: string): Promise<void> {
  const redirectTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const res = await fetch("/api/platform-lock/unlock", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password, redirectTo }),
  });
  if (!res.ok) throw new Error("Pogrešna šifra");
}

export function PlatformLockGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const status = useQuery({
    queryKey: ["platform-lock", "status"],
    queryFn: getPlatformLockStatus,
  });

  const unlockMutation = useMutation({
    mutationFn: unlockPlatform,
    onSuccess: async () => {
      setPassword("");
      setError("");
      await queryClient.invalidateQueries({
        queryKey: ["platform-lock", "status"],
      });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Pogrešna šifra");
    },
  });

  const locked = shouldRenderPlatformLockWall({
    isLoading: status.isLoading,
    isError: status.isError,
    status: status.data,
  });

  if (!locked) return <>{children}</>;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    unlockMutation.mutate(password);
  }

  return (
    <PlatformLockWall
      password={password}
      onPasswordChange={setPassword}
      onSubmit={handleSubmit}
      isSubmitting={unlockMutation.isPending || status.isLoading}
      error={error}
    />
  );
}
